import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { resolveImage } from "@/lib/api";
import { useBrands } from "./Yachts";

export const BrandLogo = ({ brand, className = "max-h-16" }) =>
  brand.logo ? (
    <img
      src={resolveImage(brand.logo)}
      alt={brand.name}
      className={`${className} w-auto object-contain mix-blend-multiply ${brand.logo_class || ""}`}
      data-testid={`brand-card-${brand.slug}-logo`}
    />
  ) : (
    <span className="font-accent text-2xl tracking-[0.35em] text-navy-ink transition-colors duration-500 group-hover:text-gold" data-testid={`brand-card-${brand.slug}-logo`}>
      {brand.wordmark || brand.name.toUpperCase()}
    </span>
  );

export default function Brands() {
  const { data: brands = [] } = useBrands();

  return (
    <section id="brands" className="relative border-t border-navy-ink/5 bg-navy px-6 py-20 lg:px-12 lg:py-28" data-testid="brands-section">
      <div className="mx-auto max-w-7xl">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-6 text-[10px] uppercase tracking-[0.45em] text-gold sm:text-xs"
          data-testid="brands-overline"
        >
          02 — Brand Partners
        </motion.p>
        <div className="mb-20 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="max-w-2xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-navy-ink sm:text-5xl lg:text-6xl"
            data-testid="brands-heading"
          >
            Marques of
            <span className="italic text-gold-light"> distinction.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="max-w-sm text-sm font-light leading-relaxed text-mist"
            data-testid="brands-description"
          >
            Modi Marine is the appointed Indian representative for a select
            portfolio of the world&rsquo;s most coveted shipyards.
          </motion.p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {brands.map((b, i) => (
            <motion.div
              key={b.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.1 }}
            >
              <Link
                to={`/yachts/${b.slug}`}
                className="group flex h-full flex-col overflow-hidden border border-navy-ink/10 bg-navy-light/40 transition-colors duration-500 hover:border-gold/40"
                data-testid={`brand-card-${b.slug}`}
              >
                <div className="relative h-40 w-full overflow-hidden bg-navy-ink" data-testid={`brand-card-${b.slug}-image`}>
                  <img
                    src={resolveImage(b.hero_image)}
                    alt={`${b.name} yacht`}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/30 to-black/20" />
                  <div className="absolute inset-0 flex items-center justify-center px-6">
                    <img
                      src={resolveImage(b.logo_light || b.logo)}
                      alt={b.name}
                      className={`max-h-10 w-auto max-w-[75%] object-contain drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)] ${b.logo_light ? "" : "brightness-0 invert"} ${b.logo_class || ""}`}
                      data-testid={`brand-card-${b.slug}-logo`}
                    />
                  </div>
                </div>
                <div className="flex flex-1 flex-col px-8 pb-8 pt-6">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{b.origin}</p>
                  <h3 className="mt-2 font-serif text-xl text-navy-ink">{b.name}</h3>
                  <p className="mt-3 text-xs font-light leading-relaxed text-mist">{b.tagline}</p>
                  <span className="mt-auto inline-flex items-center gap-2 pt-6 text-[10px] uppercase tracking-[0.3em] text-gold">
                    {b.model_count} {b.model_count === 1 ? "model" : "models"} <ArrowUpRight size={12} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
